import { useState } from 'react';
import { toast } from 'sonner';
import logger from '../utils/logger';
import {
  isAudioCached,
  downloadEventAudios,
  deleteEventAudios,
} from '../utils/audioCache';

/**
 * useAudioPreload
 *
 * Encapsula la gestión de **descarga y caché offline** de audios del evento
 * (IndexedDB): comprobar qué audios están en caché, descargar todos los del
 * evento actual con progreso, y limpiar la caché del evento.
 *
 * ⚠️ NO incluye `preloadAudio` (precarga individual de un audio para
 * reproducción) porque esa función toca estado compartido con el reproductor
 * (`audioStates`, `audioProgress`, `playingAudioId`, `isMobile`, `isPWA`) que
 * sigue viviendo en `App.js`. Extraerla requeriría lift-up de todo el player.
 *
 * @param {Object}   params
 * @param {Object}   params.currentPlaylist  — evento actual (usa `id` y `peticiones`)
 * @param {string}   params.API              — base URL del backend
 * @param {Function} params.refreshCacheStats — callback para refrescar el contador global de caché (viene de `useCacheStats`)
 */
export function useAudioPreload({ currentPlaylist, API, refreshCacheStats }) {
  const [cachedAudios, setCachedAudios] = useState({}); // { audio_id: true/false }
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({
    completed: 0,
    total: 0,
    errors: 0,
  });

  // Verifica qué audios del evento actual están en caché offline
  const checkCachedAudios = async () => {
    if (!currentPlaylist?.peticiones) return;
    // paralelizar con Promise.all (antes era secuencial con for-await)
    const relevant = currentPlaylist.peticiones.filter(
      (p) => p.is_custom_upload && p.audio_file_id
    );
    const results = await Promise.all(
      relevant.map((p) =>
        isAudioCached(p.audio_file_id).then((v) => [p.audio_file_id, v])
      )
    );
    const cached = Object.fromEntries(results);
    setCachedAudios(cached);
    logger.log(
      `📦 Audios en caché para este evento:`,
      Object.values(cached).filter(Boolean).length
    );
  };

  // Descarga y cachea todos los audios del evento actual para uso offline
  const downloadAllEventAudios = async () => {
    if (!currentPlaylist || !currentPlaylist.peticiones) {
      toast.error('No hay peticiones para descargar');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('No estás autenticado');
      return;
    }

    setDownloadingAll(true);
    setDownloadProgress({ completed: 0, total: 0, errors: 0 });
    toast.info('📥 Iniciando descarga de todos los audios...');

    try {
      const result = await downloadEventAudios(
        currentPlaylist.id,
        currentPlaylist.peticiones,
        API,
        token,
        (progress) => setDownloadProgress(progress)
      );
      await checkCachedAudios();
      await refreshCacheStats?.();

      if (result.errors === 0) {
        toast.success(
          `✅ ${result.completed} audios descargados correctamente para uso offline`
        );
      } else {
        toast.warning(
          `⚠️ ${result.completed} audios descargados, ${result.errors} errores`
        );
      }
    } catch (error) {
      console.error('Error descargando audios:', error);
      toast.error('Error descargando audios');
    } finally {
      setDownloadingAll(false);
    }
  };

  // Elimina la caché offline del evento actual
  const clearEventCache = async () => {
    if (!currentPlaylist) return;
    if (!window.confirm('¿Eliminar todos los audios descargados de este evento?')) {
      return;
    }
    try {
      await deleteEventAudios(currentPlaylist.id);
      await checkCachedAudios();
      await refreshCacheStats?.();
      toast.success('✅ Caché limpiada');
    } catch (error) {
      console.error('Error limpiando caché:', error);
      toast.error('Error limpiando caché');
    }
  };

  return {
    cachedAudios,
    setCachedAudios,
    downloadingAll,
    downloadProgress,
    checkCachedAudios,
    downloadAllEventAudios,
    clearEventCache,
  };
}
